import {
  createFrontendModule,
  microsoftAuthApiRef,
  SessionState,
  useApi,
} from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';

// create a react-query client with some default settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds should be a good default
      refetchOnWindowFocus: false, // do not refetch on window focus to avoid excessive requests
      retry: 1, // retry once on failure
    },
    mutations: {
      retry: 0, // do not retry mutations by default
    },
  },
});

// Drops all cached query data on sign-out so a second user on the same tab can never receive
// the previous user's cached results. Lives at the app root so it stays mounted across route
// changes, unlike a page-scoped listener that a sign-out could unmount before it runs.
const SignOutCacheReset = ({ children }: { children: React.ReactNode }) => {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  useEffect(() => {
    const subscription = microsoftAuth.sessionState$().subscribe(state => {
      if (state === SessionState.SignedOut) {
        queryClient.clear();
      }
    });
    return () => subscription.unsubscribe();
  }, [microsoftAuth]);
  return <>{children}</>;
};

const queryClientProviderExtension = AppRootWrapperBlueprint.make({
  name: 'query-client-provider',
  params: {
    component: ({ children }: { children: React.ReactNode }) => (
      <QueryClientProvider client={queryClient}>
        <SignOutCacheReset>{children}</SignOutCacheReset>
        <ReactQueryDevtools />
      </QueryClientProvider>
    ),
  },
});

export const reactQueryClientWrapperModule = createFrontendModule({
  pluginId: 'app',
  extensions: [queryClientProviderExtension],
});
